/**
 * Framework-agnostic fetch wrapper for the Tappya online-ordering API gateway.
 *
 * - Reads `VITE_API_BASE` from the Vite env (falls back to staging gateway).
 * - Persists access tokens in slug-scoped `localStorage`.
 * - Auto-attaches `Authorization: Bearer <token>` on every call.
 * - Throws `ApiError` (with status + parsed body) on non-2xx responses.
 */

import { scopedStorageKey } from "@/lib/storageScope";
import { payerForOrder, readPayer } from "@/lib/payer-token";
import { readParticipant } from "@/lib/participant-token";
import { isMenuOnly } from "@/lib/app-mode";
import { isBlockedInMenuOnly, MenuOnlyModeError } from "@/lib/menu-only-guard";

// First-party API hosts on *.tappya.com (custom domain → LB host-routes to the
// right gateway). Using these instead of the raw *.gateway.dev host is what lets
// the refresh-token cookie be a FIRST-PARTY cookie on .tappya.com (Domain=
// .tappya.com), so Safari/Firefox/Brave don't block it and silent token refresh
// works (no more frequent re-login).
//
// Runtime host selection — one build serves both storefront domains:
//   order-staging.tappya.com (and localhost) -> api-staging.tappya.com (staging gateway)
//   any other host                           -> api.tappya.com         (prod gateway)
// During cutover both gateways alias the same staging backends, so this is
// behavior-neutral; per-service backends flip to -prod on the prod gateway by wave.
const STAGING_BASE = "https://api-staging.tappya.com";
const PROD_BASE = "https://api.tappya.com";

const STAGING_HOSTS = new Set(["order-staging.tappya.com", "localhost", "127.0.0.1"]);
const GATEWAY_BASE =
  typeof window !== "undefined" && STAGING_HOSTS.has(window.location.hostname)
    ? STAGING_BASE
    : PROD_BASE;

// In dev: hit the Vite proxy at /api so the browser sees same-origin requests
// (the gateway's CORS allow-list rejects localhost:5174 directly). See
// vite.config.ts. In production: prefer VITE_API_BASE, else the hostname-derived
// gateway above.
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (import.meta.env.DEV ? "/api" : GATEWAY_BASE);

// Google API Gateway buffers responses and enforces a ~15s deadline, which
// kills SSE (`text/event-stream`) connections almost immediately — they come
// back as 502. We bypass the gateway for SSE and hit the online-ordering
// service's Cloud Run URL directly. CORS on that service allows the
// production storefront origins.
//
// In dev (when API_BASE is `/api`) we still route SSE through the Vite proxy
// so the browser sees same-origin; that proxy doesn't buffer the way the
// real gateway does.
//
// The base follows the SAME host split as GATEWAY_BASE above, so SSE lands on
// the same environment the REST calls already do. It used to be pinned to
// staging for every host, "until Wave 2 validates online-ordering-service-prod"
// — which meant a customer on order.tappya.com fetched from the prod service
// but streamed group-cart events from STAGING: two different datastores for one
// cart, so live updates simply never arrived in production.
//
// Wave 2 is done: the prod gateway routes /v1/online-ordering to
// online-ordering-service-prod (verified by probing a route that exists only on
// the staging image — prod answers 404 through both the gateway and the prod
// Cloud Run URL, staging answers 401). Both services allow the storefront
// origins in ALLOWED_ORIGINS. Override with VITE_SSE_BASE if a build ever needs
// to point somewhere else.
const STAGING_SSE_DIRECT =
  "https://online-ordering-service-staging-378055833376.europe-west1.run.app";
const PROD_SSE_DIRECT =
  "https://online-ordering-service-prod-jcionivtja-ew.a.run.app";

const SSE_DIRECT =
  typeof window !== "undefined" && STAGING_HOSTS.has(window.location.hostname)
    ? STAGING_SSE_DIRECT
    : PROD_SSE_DIRECT;

export const SSE_BASE: string =
  (import.meta.env.VITE_SSE_BASE as string | undefined) ??
  (import.meta.env.DEV ? API_BASE : SSE_DIRECT);

const TOKEN_STORAGE_KEY = "accessToken";

function tokenStorageKey(): string {
  return scopedStorageKey(TOKEN_STORAGE_KEY);
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(tokenStorageKey());
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(tokenStorageKey(), token);
  } catch {
    /* storage unavailable */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(tokenStorageKey());
  } catch {
    /* storage unavailable */
  }
}

// Refresh token is persisted in localStorage (not relied on as a cookie) so the
// session survives on custom tenant domains where the httpOnly refresh cookie is
// cross-site/blocked. It is replayed to /auth/refresh via the X-Refresh-Token
// header. Lower security than an httpOnly cookie (readable by XSS) — an accepted
// tradeoff for a low-value customer ordering session that must work cross-domain.
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";

function refreshTokenStorageKey(): string {
  return scopedStorageKey(REFRESH_TOKEN_STORAGE_KEY);
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(refreshTokenStorageKey());
  } catch {
    return null;
  }
}

export function setRefreshToken(token: string | null): void {
  try {
    const k = refreshTokenStorageKey();
    if (token) localStorage.setItem(k, token);
    else localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

export function clearRefreshToken(): void {
  setRefreshToken(null);
}

/**
 * Tenant API key — fetched once at boot from the public config endpoint
 * (`/v1/online-ordering/config/{urlPath}`) and then auto-injected as
 * `X-API-KEY` on subsequent calls to `/v1/online-ordering/*`. Mirrors v1's
 * `apiKeyInterceptor` + `OnlineOrderingConfigService.apiKey$`. Without it,
 * the gateway returns 401 "Not authenticated".
 */
let apiKeyMem: string | null = null;
let tenantIdMem: string | null = null;

let resolveApiKeyReady: () => void = () => {};
let apiKeyReadyPromise: Promise<void> = new Promise<void>((resolve) => {
  resolveApiKeyReady = resolve;
});

let apiKeyReadySettled = false;

export function setApiKey(key: string | null): void {
  apiKeyMem = key;
  apiKeyReadySettled = true;
  // Resolve the ready promise as soon as branch config has been resolved
  // (success or failure) — `key` may legitimately be null on auth-less
  // tenants. Auth bootstrap waits on this so /customers/me doesn't fire
  // before X-API-KEY is available.
  resolveApiKeyReady();
}

export function getApiKey(): string | null {
  return apiKeyMem;
}

/**
 * Resolves once `setApiKey()` has been called at least once (i.e. branch
 * config has been fetched, even if the tenant has no API key). Used by the
 * auth bootstrap to avoid racing the BranchProvider on /customers/me.
 */
export function whenApiKeyReady(): Promise<void> {
  return apiKeyReadyPromise;
}

/**
 * Re-close the readiness gate because a tenant config fetch is starting.
 *
 * The gate used to open once and stay open. When BranchProvider first renders
 * with no slug it calls `setApiKey(null)`, which opened the gate with neither a
 * key nor a menu link; the real config fetch for the slug was still in flight,
 * and reads fired on mount from the cached branch snapshot (review summaries,
 * promotions, blocked items) went out with no X-API-KEY / X-MENU-LINK and got
 * 403 "API key or menu link required" on digital-menu tenants. Clearing the
 * key + link and re-arming the gate makes those reads wait for the config.
 *
 * Waiters on a still-pending gate keep that same promise, so nothing hangs.
 */
export function beginConfigLoad(): void {
  apiKeyMem = null;
  menuLinkMem = null;
  if (apiKeyReadySettled) {
    apiKeyReadySettled = false;
    apiKeyReadyPromise = new Promise<void>((resolve) => {
      resolveApiKeyReady = resolve;
    });
  }
}

/** Test-only: reset the readiness gate. */
export function resetApiKeyReady(): void {
  apiKeyReadySettled = false;
  apiKeyReadyPromise = new Promise<void>((resolve) => {
    resolveApiKeyReady = resolve;
  });
}

export function setTenantId(id: string | null): void {
  tenantIdMem = id;
}

/**
 * Digital-menu tenants have no online-ordering API key: /config returns this
 * placeholder instead. It must never be sent as X-API-KEY (the backend 403s
 * it); reads send X-MENU-LINK (the link's urlPath) and the server derives the
 * tenant from the link.
 */
export const NO_API_KEY_PLACEHOLDER = "MODULE_NOT_ENABLED";
let menuLinkMem: string | null = null;

export function setMenuLink(urlPath: string | null): void {
  menuLinkMem = urlPath;
}

export function getTenantId(): string | null {
  return tenantIdMem;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/* ──────── 401 recovery: refresh-token + sign-in queue ──────── */
//
// Mirrors v1's `tokenRefreshInterceptor`. When an authenticated call to a
// /v1/online-ordering route returns 401, we:
//   1. Try to refresh the access token (single in-flight refresh; concurrent
//      401s are queued and replayed once the refresh resolves).
//   2. If the refresh succeeds, retry the original request once with the new
//      token.
//   3. If the refresh fails (no refresh cookie, expired, etc.), surface a
//      `tappya:auth-required` window event the AuthProvider listens for so
//      it can open the sign-in modal. The original request is held until
//      sign-in completes (or is cancelled), then replayed.
//
// This stops the silent "press Place order, nothing happens" failure when a
// page has been open long enough for the access token to expire.

type RefreshFn = () => Promise<{ accessToken: string }>;
let refreshFn: RefreshFn | null = null;

/** Wired by `services.ts` once after import to break the circular dep. */
export function configureAuthRefresh(fn: RefreshFn): void {
  refreshFn = fn;
}

let inflightRefresh: Promise<string | null> | null = null;
let lastRefreshAt = 0;

async function refreshOnce(): Promise<string | null> {
  if (!refreshFn) return null;
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await refreshFn!();
      const next = res?.accessToken ?? null;
      if (next) {
        setToken(next);
        lastRefreshAt = Date.now();
      }
      return next;
    } catch (err) {
      console.warn("[auth] token refresh failed", err);
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

type SignInWaiter = {
  resolve: (signedIn: boolean) => void;
};
const signInWaiters: SignInWaiter[] = [];
let signInPromptOpen = false;

function requestSignIn(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    signInWaiters.push({ resolve });
    if (!signInPromptOpen) {
      signInPromptOpen = true;
      try {
        window.dispatchEvent(new CustomEvent("tappya:auth-required"));
      } catch {
        // SSR / older browser fallback: resolve immediately as failure.
        flushSignInWaiters(false);
      }
    }
  });
}

function flushSignInWaiters(signedIn: boolean): void {
  signInPromptOpen = false;
  while (signInWaiters.length > 0) {
    const w = signInWaiters.shift()!;
    w.resolve(signedIn);
  }
}

/** Called by AuthProvider after a successful sign-in. */
export function notifySignedIn(): void {
  flushSignInWaiters(true);
}

/** Called by AuthProvider when the user dismisses the sign-in modal. */
export function notifySignInCancelled(): void {
  flushSignInWaiters(false);
}

export type ApiFetchInit = Omit<RequestInit, "body"> & {
  /** Object body (will be JSON.stringified) or pre-serialized string/FormData/Blob. */
  body?: unknown;
  /** Skip Authorization header even if a token is present. */
  skipAuth?: boolean;
  /**
   * On 401, still try a token refresh once, but if the refresh fails do NOT
   * pop the global sign-in modal — just surface the 401 to the caller. Used
   * for background bootstrap calls (e.g. /customers/me on app open) where
   * "user has no session" is a normal state and should resolve to the
   * signed-out UI, not a forced modal.
   */
  noAuthPrompt?: boolean;
};

/**
 * Append a query string to a relative path. Does NOT prepend `API_BASE` —
 * `apiFetch` is the single place that resolves the base. Doing it here too
 * would double-prefix when the result is passed back into `apiFetch`.
 */
export function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    usp.append(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

/**
 * Core fetch wrapper. Returns parsed JSON typed as `T`.
 * For 204/empty responses, returns `undefined as T`.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  return apiFetchInternal<T>(path, init, /* recovered */ false);
}

async function apiFetchInternal<T>(
  path: string,
  init: ApiFetchInit,
  recovered: boolean,
): Promise<T> {
  const { body, skipAuth, noAuthPrompt, headers, ...rest } = init;

  // Menu-only links (menu link `type: digitalMenu`) can browse and shortlist
  // but cannot order or hold an account. UI gating hides the entry points;
  // this is the backstop for anything that slips past it — a restored tab, a
  // stale bookmark, a screen firing on mount before its route guard renders.
  // Checked before the API-key await below so a blocked call fails fast
  // instead of parking on the bootstrap gate.
  if (isMenuOnly() && isBlockedInMenuOnly(path, rest.method)) {
    throw new MenuOnlyModeError(path, rest.method);
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const isOnlineOrderingRoute = path.includes("/v1/online-ordering");
  const isRefreshCall = path.includes("/auth/refresh");
  // The public-config endpoint is the one BranchProvider uses to *fetch*
  // the X-API-KEY itself, so it must be allowed through without waiting
  // for the key — otherwise we deadlock the whole app on first paint.
  const isConfigBootstrap = path.includes("/v1/online-ordering/config/");

  // Returning from external 3DS reloads the app and mounts route screens before
  // BranchProvider has necessarily fetched the public config. Online-ordering
  // calls need that config because it supplies X-API-KEY (the tenant
  // identifier required by the gateway, even on `skipAuth: true` routes like
  // /auth/send-otp). Wait here instead of letting individual screens race and
  // surface a 401 — or, in the send-otp case, hang on a missing tenant key
  // and stick the sign-in modal on "Sending…".
  if (isOnlineOrderingRoute && !apiKeyMem && !isConfigBootstrap) {
    await whenApiKeyReady();
    // The config that released the gate also published the app mode; a call
    // made before it landed (mode "unresolved") gets the menu-only check now.
    if (isMenuOnly() && isBlockedInMenuOnly(path, rest.method)) {
      throw new MenuOnlyModeError(path, rest.method);
    }
  }

  const finalHeaders = new Headers(headers ?? {});
  // Default to JSON
  if (!finalHeaders.has("Accept")) {
    finalHeaders.set("Accept", "application/json");
  }

  let serializedBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (
      typeof body === "string" ||
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      body instanceof URLSearchParams
    ) {
      serializedBody = body as BodyInit;
    } else {
      serializedBody = JSON.stringify(body);
      if (!finalHeaders.has("Content-Type")) {
        finalHeaders.set("Content-Type", "application/json");
      }
    }
  }

  let usedToken: string | null = null;
  if (!skipAuth) {
    const token = getToken();
    if (token && !finalHeaders.has("Authorization")) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
      usedToken = token;
    }
  }

  // Payer-token attach (dine-in diners, spec 2026-07-20 §4/§7 as amended by
  // 2026-07-22 §5.1). The payer token is a *table credential*: it authorizes
  // reads and split mutations for one order. The customer JWT is an *identity*.
  // The two are complementary, so we send BOTH when both exist:
  //  - No customer token → the payer token IS the Authorization bearer
  //    (fixes anonymous-diner 401s on orderApi.get for solo/POS orders).
  //  - Customer token present (a signed-in diner who does NOT own the order)
  //    → the customer token keeps Authorization and the payer token rides
  //    `X-Participant-Token`. Without this the request fell through to the
  //    BFF's customer branch, ownership failed, and a signed-in diner got a
  //    404 on their own table's bill — strictly worse access than anonymous.
  //    The BFF now accepts a payer-shaped payload (orderId, no groupCartId)
  //    in that header and, per §4, still attributes the split to the customer.
  //  - A stored participant token always wins the X-Participant-Token slot
  //    (its call-sites attach it explicitly), and caller-set headers are
  //    never overwritten. usedToken deliberately stays null for payer
  //    attaches so the 401 refresh/sign-in recovery never fires for them.
  //
  // Payer tokens are ORDER-SCOPED — the payload carries `orderId` and the
  // BFF 401s a token whose orderId doesn't match the target order. A diner
  // who established a payer session for order A must NOT have that token
  // attached to a later request about order B, so we only attach when the
  // request can plausibly be for the payer's own order:
  //  - The path names an `/orders/{orderId}` segment → attach only if it's
  //    the payer's own orderId (covers /orders/{id}, /orders/{id}/status,
  //    /payments/orders/{id}/splits).
  //  - POST /payments/splits (create) carries the orderId in the BODY, not
  //    the path → scope the token against the body's orderId, exactly as we
  //    would a path match. (Without this the create call — the one that
  //    starts every split — would go out unauthenticated.)
  //  - The path is a split-scoped mutation that carries no orderId anywhere
  //    (/payments/splits/{splitId}/checkout|mark-cash-pending|release, or
  //    PATCH /payments/splits/{splitId}) → attach the stored payer token;
  //    the BFF resolves identity from the token and payment_service
  //    enforces per-split ownership, so a mismatched token fails closed
  //    there instead of leaking.
  //  - Anything else (e.g. no order in scope yet) → do not attach; this is
  //    the stale-token case that previously guaranteed a 401.
  if (isOnlineOrderingRoute) {
    const orderIdMatch = path.match(/\/orders\/([^/?]+)/);
    let payer = orderIdMatch
      ? payerForOrder(decodeURIComponent(orderIdMatch[1]))
      : null;
    if (!payer) {
      if (/\/payments\/splits(\?|$)/.test(path)) {
        const bodyOrderId =
          body && typeof body === "object" && !Array.isArray(body)
            ? (body as { orderId?: unknown }).orderId
            : undefined;
        payer =
          typeof bodyOrderId === "string" ? payerForOrder(bodyOrderId) : null;
      } else if (/\/payments\/splits\/[^/?]+/.test(path)) {
        payer = readPayer();
      }
    }
    if (payer && !skipAuth) {
      if (!finalHeaders.has("Authorization")) {
        finalHeaders.set("Authorization", `Bearer ${payer.payerToken}`);
      } else if (
        !finalHeaders.has("X-Participant-Token") &&
        !readParticipant()
      ) {
        // Authorization is taken (customer token, or the caller set one) —
        // ride the second slot so the table credential still reaches the BFF.
        finalHeaders.set("X-Participant-Token", `Bearer ${payer.payerToken}`);
      }
    }
  }

  // Auto-inject X-API-KEY for online-ordering routes once the tenant config
  // has been resolved. Mirrors v1's apiKeyInterceptor.
  const hasRealApiKey = !!apiKeyMem && apiKeyMem !== NO_API_KEY_PLACEHOLDER;
  if (
    hasRealApiKey &&
    !finalHeaders.has("X-API-KEY") &&
    isOnlineOrderingRoute
  ) {
    finalHeaders.set("X-API-KEY", apiKeyMem as string);
  } else if (!hasRealApiKey && menuLinkMem && isOnlineOrderingRoute && !isConfigBootstrap) {
    // No key (digital-menu tenant): identify by menu link for the read-only
    // menu routes the backend serves on it.
    finalHeaders.set("X-MENU-LINK", menuLinkMem);
  }

  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: serializedBody,
    // Send cookies so the refresh-token httpOnly cookie reaches the gateway.
    credentials: rest.credentials ?? "include",
  });

  // Try to parse a body for both success + error paths
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // 401 recovery: refresh once, then prompt sign-in. Skip if we already
    // recovered this call, if the request was unauthenticated, or if the
    // refresh endpoint itself failed.
    //
    // CRITICAL: only run recovery when the request actually carried a token.
    // A 401 on a tokenless call (e.g. public `/promotions/active` returning
    // an auth error before X-API-KEY is set) is not an expired-session
    // signal — running refresh + popping the sign-in modal would be wrong.
    const canRecover =
      res.status === 401 &&
      !recovered &&
      !skipAuth &&
      !isRefreshCall &&
      isOnlineOrderingRoute &&
      usedToken !== null;
    if (canRecover) {
      // Stale-token short-circuit: a parallel call may have already refreshed.
      const current = getToken();
      const timeSinceRefresh = Date.now() - lastRefreshAt;
      if (current && current !== usedToken && timeSinceRefresh < 30_000) {
        return apiFetchInternal<T>(path, init, /* recovered */ true);
      }

      const next = await refreshOnce();
      if (next) {
        return apiFetchInternal<T>(path, init, /* recovered */ true);
      }

      // Refresh failed. For background calls (`noAuthPrompt`), surface the
      // 401 silently so the caller can clear local auth state without
      // hijacking the user's first paint with a sign-in modal. Otherwise,
      // ask the user to sign in and retry once.
      if (!noAuthPrompt) {
        const signedIn = await requestSignIn();
        if (signedIn) {
          return apiFetchInternal<T>(path, init, /* recovered */ true);
        }
      }
    }
    throw new ApiError(res.status, parsed, `${rest.method ?? "GET"} ${url} failed`);
  }

  return parsed as T;
}
